/**
 * ChatDataContext - Compatibility layer for Message.tsx and other components
 *
 * This provides a compatible interface that maps to the new CopilotChatContext.
 * This allows existing components to work without extensive refactoring.
 */

import React, { createContext, useContext, ReactNode, useCallback, useState, useEffect, useRef, useMemo } from 'react';
import useCopilotChatContext from '../hooks/useCopilotChatContext';
import {
  CopilotLocation,
  CopilotServiceArea,
  CopilotCatalog,
  CopilotCustomerProfile,
} from '../context/CopilotChatContext';

export type ChannelType = 'online' | 'instore' | 'both' | 'digital';

// Interface for product that matches what Message.tsx expects
export interface Product {
  id?: string;
  catalogId?: string;
  name: string;
  description?: string;
  price?: string | number;
  category?: string;
  kind?: string;
  image?: string;
  url?: string;
}

// Generated logo interface
export interface GeneratedLogo {
  id: string;
  url: string;
  style: string;
}

// Design Direction interface
export interface DesignDirection {
  id: string;
  variant: string;
  status: string;
  locked?: boolean;
  attributes?: {
    elements?: Array<{
      source?: string;
      [key: string]: unknown;
    }>;
    [key: string]: unknown;
  };
}

interface ChatDataContextType {
  // Locations (mapped from currentLocations)
  locations: CopilotLocation[];

  // Service Areas (mapped from currentServiceAreas)
  serviceAreas: CopilotServiceArea[];

  // Products (mapped from currentCatalogs)
  products: Product[];

  // Customer Profiles (mapped from currentCustomerProfiles)
  customerProfiles: CopilotCustomerProfile[];

  // Selected catalog
  selectedCatalogId: string | null;
  setSelectedCatalogId: (id: string | null) => void;

  // Channel selection
  selectedChannel: ChannelType | null;
  setSelectedChannel: (channel: ChannelType | null) => void;

  // Generated logos
  generatedLogos: GeneratedLogo[];
  pendingLogoCount: number; // Number of logos still being generated
  selectedLogoId: string | null;
  setSelectedLogoId: (id: string | null) => void;

  // Product suggestion selection (for clickable examples)
  selectedSuggestionIndex: number | null;
  setSelectedSuggestionIndex: (index: number | null) => void;

  // Bordered container selection - stores the content of the clicked container
  clickedBorderedContent: string | null;
  setClickedBorderedContent: (content: string | null) => void;

  // Chat callbacks (no-op in new architecture)
  setChatCallbacks: (callbacks: Record<string, any>) => void;

  // Remove functions
  removeProduct: (id: string) => void;
  removeLocation: (id: string) => void;
  removeServiceArea: (id: string) => void;
  onProductRemoved?: (id: string) => void;

  // Location modal state
  setEditingLocation: (location: CopilotLocation | null) => void;
  setLocationModalOpen: (open: boolean) => void;
  isLocationRemoved: (id: string) => boolean;

  // Service Area modal state
  setEditingServiceArea: (area: CopilotServiceArea | null) => void;
  setServiceAreaModalOpen: (open: boolean) => void;
  isServiceAreaRemoved: (id: string) => boolean;

  // Design Directions
  designDirections: DesignDirection[];
  setDesignDirections: (dds: DesignDirection[]) => void;
  selectedDesignDirectionId: string | null;
  setSelectedDesignDirectionId: (id: string | null) => void;
  isGeneratingDD: boolean;
  setIsGeneratingDD: (generating: boolean) => void;
}

const ChatDataContext = createContext<ChatDataContextType | undefined>(undefined);

export const useChatData = (): ChatDataContextType => {
  const context = useContext(ChatDataContext);
  if (!context) {
    throw new Error('useChatData must be used within a ChatDataProvider');
  }
  return context;
};

interface ChatDataProviderProps {
  children: ReactNode;
}

/**
 * ChatDataProvider - Compatibility provider that wraps CopilotChatContext
 */
export const ChatDataProvider: React.FC<ChatDataProviderProps> = ({ children }) => {
  const copilotContext = useCopilotChatContext();

  // Use editing state from shared context
  const { editingLocation, setEditingLocation, editingServiceArea, setEditingServiceArea } = copilotContext;

  // Track removed items (for isLocationRemoved / isServiceAreaRemoved checks)
  const [removedLocationIds] = useState<Set<string>>(new Set());
  const [removedServiceAreaIds] = useState<Set<string>>(new Set());

  // Selected logo state (local UI state, not persisted to backend until selection is confirmed)
  const [selectedLogoId, setSelectedLogoId] = useState<string | null>(null);

  // Selected product suggestion state (for clickable example selection)
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<number | null>(null);

  // Bordered container selection state - stores content of clicked container
  const [clickedBorderedContent, setClickedBorderedContent] = useState<string | null>(null);

  // Design Directions state
  const [designDirections, setDesignDirections] = useState<DesignDirection[]>([]);
  const [selectedDesignDirectionId, setSelectedDesignDirectionId] = useState<string | null>(null);
  const [isGeneratingDD, setIsGeneratingDD] = useState<boolean>(false);

  // Reset suggestion selection when products are added (new message displayed)
  const prevProductsCountRef = useRef<number>(0);
  useEffect(() => {
    const currentCount = copilotContext.currentCatalogs?.length || 0;
    // If products were added, reset the suggestion selection
    if (currentCount > prevProductsCountRef.current) {
      setSelectedSuggestionIndex(null);
    }
    prevProductsCountRef.current = currentCount;
  }, [copilotContext.currentCatalogs]);

  // Get generatedLogos and pendingLogoCount from onboardingState - memoized to prevent re-renders
  const generatedLogos: GeneratedLogo[] = useMemo(() => {
    return copilotContext.onboardingState?.generatedLogos || [];
  }, [copilotContext.onboardingState?.generatedLogos]);
  const pendingLogoCount: number = copilotContext.onboardingState?.pendingLogoCount || 0;

  // Debug logging for logo state - only log when count changes
  const prevLogosCountRef = useRef<number>(0);
  useEffect(() => {
    if (prevLogosCountRef.current !== generatedLogos.length) {
      console.log('[ChatDataContext] generatedLogos updated:', {
        count: generatedLogos.length,
        logos: generatedLogos.map(l => ({ id: l.id, style: l.style, url: l.url?.substring(0, 50) + '...' })),
        pendingLogoCount,
      });
      prevLogosCountRef.current = generatedLogos.length;
    }
  }, [generatedLogos, pendingLogoCount]);

  // Track the first logo URL to detect when new logos are generated
  const prevFirstLogoUrlRef = useRef<string | null>(null);

  // Clear selectedLogoId when new logos are generated
  useEffect(() => {
    const currentFirstLogoUrl = generatedLogos[0]?.url || null;

    // If the first logo URL changed, new logos were generated - clear selection
    if (prevFirstLogoUrlRef.current !== null && currentFirstLogoUrl !== prevFirstLogoUrlRef.current) {
      console.log('[ChatDataContext] New logos detected, clearing selectedLogoId');
      setSelectedLogoId(null);
    }

    prevFirstLogoUrlRef.current = currentFirstLogoUrl;
  }, [generatedLogos]);

  // Map currentCatalogs to products format - memoized to prevent unnecessary re-renders
  const products: Product[] = useMemo(() => {
    return (copilotContext.currentCatalogs || []).map((catalog: CopilotCatalog) => ({
      id: catalog.id || catalog.catalogId,
      catalogId: catalog.catalogId,
      name: catalog.name,
      description: catalog.description,
      price: catalog.price,
      image: catalog.image || catalog.imageUrl,
      category: catalog.type,
      kind: catalog.kind || catalog.type || 'product',
    }));
  }, [copilotContext.currentCatalogs]);

  // Debug logging for products - only log when currentCatalogs actually changes
  const prevCatalogsLengthRef = useRef<number>(0);
  useEffect(() => {
    const currentLength = copilotContext.currentCatalogs?.length || 0;
    // Only log if the count actually changed
    if (prevCatalogsLengthRef.current !== currentLength) {
      console.log('[ChatDataContext] products updated:', {
        count: products.length,
        products: products.map(p => ({ id: p.id, catalogId: p.catalogId, name: p.name })),
        currentCatalogsCount: currentLength,
      });
      prevCatalogsLengthRef.current = currentLength;
    }
  }, [products, copilotContext.currentCatalogs]);

  // Map selectedChannel from onboardingState
  const selectedChannel = copilotContext.onboardingState?.channel || null;

  // No-op setSelectedChannel - channel is now managed by backend
  const setSelectedChannel = useCallback((channel: ChannelType | null) => {
    console.log('[ChatDataContext compat] setSelectedChannel called:', channel);
  }, []);

  // Get selectedCatalogId from onboardingState
  const selectedCatalogId = copilotContext.onboardingState?.selectedCatalogId || null;

  // No-op setChatCallbacks - callbacks are no longer used
  const setChatCallbacks = useCallback((_callbacks: Record<string, any>) => {
    // No-op in new architecture
  }, []);

  // Map removeCatalog to removeProduct
  const removeProduct = copilotContext.removeCatalog;
  const removeLocation = copilotContext.removeLocation;
  const removeServiceArea = copilotContext.removeServiceArea;

  // Check functions
  const isLocationRemoved = useCallback((id: string) => removedLocationIds.has(id), [removedLocationIds]);
  const isServiceAreaRemoved = useCallback((id: string) => removedServiceAreaIds.has(id), [removedServiceAreaIds]);

  // Memoize the context value to prevent unnecessary re-renders
  const value: ChatDataContextType = useMemo(() => ({
    locations: copilotContext.currentLocations || [],
    serviceAreas: copilotContext.currentServiceAreas || [],
    products,
    customerProfiles: copilotContext.currentCustomerProfiles || [],
    selectedCatalogId,
    setSelectedCatalogId: copilotContext.setSelectedCatalogId,
    selectedChannel,
    setSelectedChannel,
    generatedLogos,
    pendingLogoCount,
    selectedLogoId,
    setSelectedLogoId,
    selectedSuggestionIndex,
    setSelectedSuggestionIndex,
    clickedBorderedContent,
    setClickedBorderedContent,
    setChatCallbacks,
    removeProduct,
    removeLocation,
    removeServiceArea,
    onProductRemoved: removeProduct,
    setEditingLocation,
    setLocationModalOpen: copilotContext.setLocationModalOpen,
    isLocationRemoved,
    setEditingServiceArea,
    setServiceAreaModalOpen: copilotContext.setServiceAreaModalOpen,
    isServiceAreaRemoved,
    // Design Directions
    designDirections,
    setDesignDirections,
    selectedDesignDirectionId,
    setSelectedDesignDirectionId,
    isGeneratingDD,
    setIsGeneratingDD,
  }), [
    copilotContext.currentLocations,
    copilotContext.currentServiceAreas,
    products,
    copilotContext.currentCustomerProfiles,
    selectedCatalogId,
    copilotContext.setSelectedCatalogId,
    selectedChannel,
    setSelectedChannel,
    generatedLogos,
    pendingLogoCount,
    selectedLogoId,
    selectedSuggestionIndex,
    clickedBorderedContent,
    setChatCallbacks,
    removeProduct,
    removeLocation,
    removeServiceArea,
    setEditingLocation,
    copilotContext.setLocationModalOpen,
    isLocationRemoved,
    setEditingServiceArea,
    copilotContext.setServiceAreaModalOpen,
    isServiceAreaRemoved,
    // Design Directions
    designDirections,
    selectedDesignDirectionId,
    isGeneratingDD,
  ]);

  return (
    <ChatDataContext.Provider value={value}>
      {children}
    </ChatDataContext.Provider>
  );
};

export default ChatDataContext;
